// frontend/src/api/valuationB3.ts
//
// VALUATION-B3 — typed client for the tunable methodology store, the industry
// picker, and the finalize/sign-off surface.
//
// Same shape as api/valuation.ts: thin wrappers around `apiClient`
// (baseURL = VITE_API_BASE_URL + '/api'), so paths are relative to `/api` and
// resolve to apps/api/routers/_valuation_config.py and _valuation_finalize.py.
//
// As in api/valuation.ts, there is no valuation math here and there must never
// be. That matters more on this surface than on any other: the tuning screen's
// entire job is to show what the ENGINE does with a changed weight. A number
// this file worked out for itself would be a second methodology.

import { apiClient } from '@/api/client'
import type {
  ConfigResponse,
  FinalizeBody,
  FinalizeResponse,
  IndustriesResponse,
  IndustrySearchResponse,
  PreflightResponse,
  RecomputeBody,
  RecomputeResponse,
  SnapshotHistoryResponse,
  SnapshotResponse,
  TuningBody,
} from '@/types/valuationB3'
import type { DraftResponse, SaveDraftBody, SaveDraftResponse } from '@/types/valuationDraft'

const BASE = '/v1/valuation'
const eng = (id: string) => `${BASE}/engagements/${id}`

// ── config store ──────────────────────────────────────────────────────────────

/** The active methodology for the caller's firm, with its fingerprint. */
export async function fetchActiveConfig(): Promise<ConfigResponse> {
  const { data } = await apiClient.get<ConfigResponse>(`${BASE}/config`)
  return data
}

export async function fetchConfigVersion(version: string): Promise<ConfigResponse> {
  const { data } = await apiClient.get<ConfigResponse>(
    `${BASE}/config/${encodeURIComponent(version)}`,
  )
  return data
}

/**
 * Register a tuned config as a firm-wide candidate version.
 *
 * Distinct from passing `tuning` to /recompute: that tunes ONE engagement's
 * preview and freezes into that engagement's record. This changes the house
 * default for every engagement opened after it — hence CPA-only, and hence
 * `version` and `rationale` are both required rather than optional.
 */
export async function registerConfigCandidate(
  body: TuningBody & { version: string; rationale: string; activate?: boolean },
): Promise<ConfigResponse> {
  const { data } = await apiClient.post<ConfigResponse>(`${BASE}/config/candidate`, body)
  return data
}

// ── industry picker ───────────────────────────────────────────────────────────

/** All 12 verticals, each carrying `provisional` so it can be marked up front. */
export async function fetchIndustries(): Promise<IndustriesResponse> {
  const { data } = await apiClient.get<IndustriesResponse>(`${BASE}/industries`)
  return data
}

/**
 * One box, two behaviours — a digits-only query resolves as a NAICS code,
 * anything else searches plain language. The server decides which; the caller
 * passes the raw text through and renders whichever half comes back.
 */
export async function searchIndustries(
  q: string,
  limit = 8,
): Promise<IndustrySearchResponse> {
  const { data } = await apiClient.get<IndustrySearchResponse>(`${BASE}/industries/search`, {
    params: { q, limit },
  })
  return data
}

// ── recompute / preflight / finalize ──────────────────────────────────────────

/**
 * Preview one run. Never persists, and never clamps: an out-of-range CPA
 * override comes back at its stated value with `override_out_of_range` set and
 * the band widened — CC#5 behaviour, untouched. The fence is at finalize.
 * VAL-PATH P5: with no body the server runs the SAVED draft (step 4).
 */
export async function recomputeValuation(
  id: string,
  body?: RecomputeBody,
): Promise<RecomputeResponse> {
  const { data } = await apiClient.post<RecomputeResponse>(`${eng(id)}/recompute`, body)
  return data
}

/**
 * Can this be signed? `blockers` is the list the Sign-off button is disabled on.
 * With no body the server checks the SAVED draft (VAL-FINALIZE-FROM-SAVED-1A);
 * a body is still accepted for callers that preview unsaved inputs.
 */
export async function preflightValuation(
  id: string,
  body?: RecomputeBody,
): Promise<PreflightResponse> {
  const { data } = await apiClient.post<PreflightResponse>(`${eng(id)}/preflight`, body)
  return data
}

/**
 * Freeze the record. `signed_by` is NOT sent — the server takes it from the
 * verified token, because a sign-off a caller can address to someone else is
 * not a sign-off.
 */
export async function finalizeValuation(
  id: string,
  rationale: string,
): Promise<FinalizeResponse> {
  const body: FinalizeBody = { rationale }
  const { data } = await apiClient.post<FinalizeResponse>(`${eng(id)}/finalize`, body)
  return data
}

/**
 * The frozen record, replayed through its own frozen config on read —
 * `verification.reproduced` is the reproducibility claim, checked rather than
 * asserted. `version` fetches a superseded record; omitted returns the live one.
 */
export async function fetchSnapshot(
  id: string,
  version?: number,
): Promise<SnapshotResponse> {
  const { data } = await apiClient.get<SnapshotResponse>(`${eng(id)}/snapshot`, {
    params: version === undefined ? undefined : { version },
  })
  return data
}

/** Sign-off history — newest first, superseded versions retained, never deleted. */
export async function fetchSnapshotHistory(id: string): Promise<SnapshotHistoryResponse> {
  const { data } = await apiClient.get<SnapshotHistoryResponse>(`${eng(id)}/snapshots`)
  return data
}

// ── draft ─────────────────────────────────────────────────────────────────────

/**
 * The one working draft for this engagement — `inputs: null` on a fresh
 * engagement, never a 404 (see `_valuation_draft.py`). Note the wire
 * asymmetry documented on `DraftInputs`: this response's driver-answer key
 * is `answers`, not `driver_answers` — they are different shapes.
 */
export async function fetchValuationDraft(id: string): Promise<DraftResponse> {
  const { data } = await apiClient.get<DraftResponse>(`${eng(id)}/draft`)
  return data
}

/**
 * Merge-on-write: send only the fields this screen owns. Fields the caller
 * omits are left untouched server-side, not cleared — so Tuning saving its
 * half must never re-send `income_value`/`total_assets`/`total_liabilities`
 * as null just because it has no such fields, and vice versa for Intake.
 * The one exception the server enforces: a brand-new draft's first save
 * must include both `benchmark_key` and `normalized_earnings`, or it 400s.
 */
export async function saveValuationDraft(
  id: string,
  body: SaveDraftBody,
): Promise<SaveDraftResponse> {
  const { data } = await apiClient.put<SaveDraftResponse>(`${eng(id)}/draft`, body)
  return data
}

// ── produce document ──────────────────────────────────────────────────────────

const FILENAME_RE = /filename="?([^"]+)"?/

/** One archived production event, as returned by the produce endpoint. */
export interface ProducedDocumentRow {
  document_id: string
  media_type: string
  filename: string
  byte_size: number
  sha256: string
  blob_status: string
}

/** The produce endpoint's response: what got archived, not the bytes themselves. */
export interface ProduceDocumentResponse {
  snapshot_version: number
  pdf_status: string
  documents: ProducedDocumentRow[]
}

/**
 * Produce the calculated-value document(s) for a signed snapshot.
 *
 * The gate lives on the server: an unsigned or superseded record comes back 409
 * with the gate's own wording. That arrives as ordinary JSON, so callers use
 * `formatApiError` rather than the blob-decoding helper below.
 *
 * Returns the archive rows for what was produced — `.docx` always, `.pdf`
 * alongside it when conversion succeeded — never the bytes. The caller
 * downloads each row individually via `downloadArchivedDocument`.
 */
export async function produceValuationDocument(
  id: string,
  version?: number,
): Promise<ProduceDocumentResponse> {
  const { data } = await apiClient.get<ProduceDocumentResponse>(`${eng(id)}/snapshot/document`, {
    params: version === undefined ? undefined : { version },
    timeout: 60_000,
  })
  return data
}

/**
 * Download one already-archived document by id. Same reasoning as the old
 * `downloadValuationDocument`: `responseType` is 'blob', so a refusal's error
 * envelope arrives as a Blob too and is decoded back to text here — otherwise
 * the caller would surface "[object Blob]" instead of the one sentence
 * explaining why nothing came back.
 *
 * Returns the server's filename, which carries the record version. Renaming it
 * client-side would let two documents frozen on different bases sit in one
 * folder looking interchangeable.
 */
export async function downloadArchivedDocument(
  engagementId: string,
  documentId: string,
): Promise<{ blob: Blob; filename: string }> {
  try {
    const res = await apiClient.get(`${eng(engagementId)}/documents/${documentId}`, {
      responseType: 'blob',
      timeout: 60_000,
    })
    const disposition = String(res.headers?.['content-disposition'] ?? '')
    const match = FILENAME_RE.exec(disposition)
    return {
      blob: res.data as Blob,
      filename: match?.[1] ?? `valuation_document_${documentId}`,
    }
  } catch (err) {
    const message = await messageFromBlobError(err)
    if (message) throw new Error(message)
    throw err
  }
}

/** VAL-SNAPSHOT-2: the paid Snapshot (PDF; docx if conversion failed). Refusals come back as the one plain sentence. */
export async function downloadSnapshot(
  engagementId: string,
): Promise<{ blob: Blob; filename: string }> {
  try {
    const res = await apiClient.get(`${eng(engagementId)}/snapshot/estimate-document`, {
      responseType: 'blob',
      timeout: 60_000,
    })
    const match = FILENAME_RE.exec(String(res.headers?.['content-disposition'] ?? ''))
    return { blob: res.data as Blob, filename: match?.[1] ?? 'Snapshot.pdf' }
  } catch (err) {
    const message = await messageFromBlobError(err)
    if (message) throw new Error(message)
    throw err
  }
}

/**
 * Pull the envelope's `error.message` back out of a Blob response body.
 *
 * `{"error": {"code", "message", ...}}` is the platform envelope (main.py's
 * YansarException handler); `detail` is FastAPI's own shape, which a 422 on the
 * `version` query constraint still uses. Anything unparseable returns null so
 * the caller falls back to the interceptor's `translatedMessage`.
 */
async function messageFromBlobError(err: unknown): Promise<string | null> {
  const body = (err as { response?: { data?: unknown } }).response?.data
  if (!(body instanceof Blob)) return null
  try {
    const parsed = JSON.parse(await body.text()) as {
      error?: { message?: unknown }
      detail?: unknown
    }
    const message = parsed.error?.message ?? parsed.detail
    return typeof message === 'string' && message ? message : null
  } catch {
    return null
  }
}

